<template>
  <div class="pq-root">
    <!-- Header controls -->
    <div class="pq-header">
      <div class="pq-mode-group">
        <span class="pq-label">Schedule Mode:</span>
        <select v-model="localMode" class="pq-select" :disabled="executing">
          <option value="manual">Manual</option>
          <option value="timed">Timed (at datetime)</option>
          <option value="delay">Delay after previous</option>
          <option value="rotation">Rotation slots</option>
        </select>
      </div>
      <div class="pq-actions">
        <button class="btn btn-sm btn-outline-secondary mr-2"
                :disabled="!transfers.length || executing"
                @click="exportCsv">Export CSV</button>
        <button class="btn btn-sm btn-danger"
                :disabled="!executableCount || executing"
                @click="$emit('execute-all')">
          <span v-if="executing" class="pq-spin mr-1"></span>
          {{ executing ? 'Executing…' : 'Execute All (' + executableCount + ')' }}
        </button>
      </div>
    </div>

    <!-- Transfer rows -->
    <div v-if="!transfers.length" class="pq-empty">
      No transfers queued — add target addresses on the left.
    </div>

    <transition-group tag="div" name="pq-fade" class="pq-list">
      <div v-for="t in transfers" :key="t.id"
           class="pq-row" :class="rowClass(t)"
           @click="$emit('select', t.id)">

        <!-- Order handle + index -->
        <span class="pq-index">#{{ transfers.indexOf(t) + 1 }}</span>

        <!-- AML badge -->
        <span v-if="t.amlResult" class="pq-aml-dot"
              :style="{ background: amlColor(t) }"
              :title="amlTitle(t)"></span>
        <span v-else class="pq-aml-dot pq-aml-unknown" title="Not checked"></span>

        <!-- Address -->
        <span class="pq-addr" :title="t.address">{{ short(t.address) }}</span>
        <span v-if="t.label" class="pq-label-tag">{{ t.label }}</span>

        <!-- Amount -->
        <input v-if="!executing"
               type="number" min="0" step="any"
               class="pq-amount-input"
               :value="t.amount"
               @change="$emit('update-amount', { id: t.id, amount: $event.target.value })"
               @click.stop />
        <span v-else class="pq-amount-text">{{ t.amount }}</span>
        <span class="pq-usdt">USDT</span>

        <!-- Schedule control -->
        <div class="pq-schedule" @click.stop>
          <template v-if="localMode === 'timed'">
            <input type="datetime-local" class="pq-datetime"
                   :value="t.scheduledAt ? toDatetimeLocal(t.scheduledAt) : ''"
                   :disabled="executing"
                   @change="$emit('update-schedule', { id: t.id, scheduledAt: new Date($event.target.value).getTime() })" />
            <span v-if="t.scheduledAt && countdown(t.scheduledAt)" class="pq-countdown">
              {{ countdown(t.scheduledAt) }}
            </span>
          </template>
          <template v-else-if="localMode === 'delay'">
            <span class="pq-delay-label">+</span>
            <input type="number" min="0" step="1" class="pq-delay-input"
                   :value="t.delayMinutes || 0"
                   :disabled="executing"
                   @change="$emit('update-schedule', { id: t.id, delayMinutes: Number($event.target.value) })" />
            <span class="pq-delay-unit">min</span>
          </template>
          <template v-else-if="localMode === 'rotation'">
            <input type="text" class="pq-rotation-input"
                   placeholder="e.g. Mon 09:00"
                   :value="t.rotationSlot || ''"
                   :disabled="executing"
                   @change="$emit('update-schedule', { id: t.id, rotationSlot: $event.target.value })" />
          </template>
          <span v-else class="pq-manual-tag">Manual</span>
        </div>

        <!-- Status pill -->
        <span class="pq-status" :class="'pq-status-' + t.status">
          {{ statusLabel(t.status) }}
        </span>

        <!-- Action buttons -->
        <div class="pq-btns" @click.stop>
          <button v-if="!executing && canSend(t)"
                  class="btn btn-xs btn-outline-success"
                  @click="$emit('send-now', t.id)">▶ Send</button>
          <button v-if="!executing"
                  class="btn btn-xs btn-outline-danger"
                  @click="$emit('remove', t.id)">✕</button>
        </div>
      </div>
    </transition-group>

    <!-- Log section -->
    <div v-if="log.length" class="pq-log">
      <div class="pq-log-header">Transfer Log ({{ log.length }})</div>
      <div v-for="(entry, i) in log.slice().reverse()" :key="i" class="pq-log-entry">
        <span class="pq-log-time">{{ fmtTime(entry.timestamp) }}</span>
        <span :class="'pq-log-' + entry.type">{{ entry.message }}</span>
        <a v-if="entry.txid" :href="explorerLink(entry)" target="_blank"
           class="pq-log-link">{{ short(entry.txid) }}</a>
      </div>
    </div>
  </div>
</template>

<script>
import { RISK_COLOR } from '../services/amlService';

const STATUS_LABEL = {
  pending:       'Pending',
  scheduled:     'Scheduled',
  waiting_ledger:'Awaiting Ledger',
  broadcasting:  'Broadcasting',
  confirmed:     'Confirmed',
  failed:        'Failed',
  blocked:       'Blocked',
  active:        'In Progress',
};

const EXPLORER = {
  tron:     'https://tronscan.org/#/transaction/',
  ethereum: 'https://etherscan.io/tx/',
  bsc:      'https://bscscan.com/tx/',
  polygon:  'https://polygonscan.com/tx/',
};

export default {
  name: 'PayoutQueue',
  props: {
    transfers:  { type: Array,  default: () => [] },
    log:        { type: Array,  default: () => [] },
    executing:  { type: Boolean,default: false },
    mode:       { type: String, default: 'manual' },
    chain:      { type: String, default: 'tron' },
  },
  emits: [
    'execute-all', 'send-now', 'remove', 'select',
    'update-amount', 'update-schedule', 'update-mode',
  ],

  data() {
    return { localMode: this.mode, tick: 0, tickTimer: null };
  },

  computed: {
    executableCount() {
      return this.transfers.filter(t =>
        ['pending', 'scheduled', 'failed'].includes(t.status) && this.canSend(t)
      ).length;
    },
  },

  watch: {
    mode(v) { this.localMode = v; },
    localMode(v) { this.$emit('update-mode', v); },
  },

  mounted() {
    this.tickTimer = setInterval(() => { this.tick++; }, 1000);
  },
  beforeDestroy() { clearInterval(this.tickTimer); },

  methods: {
    short(addr) {
      if (!addr) return '—';
      return addr.length > 14 ? addr.slice(0, 7) + '…' + addr.slice(-5) : addr;
    },
    statusLabel(s) { return STATUS_LABEL[s] || s || 'Pending'; },

    canSend(t) {
      if (t.amlResult && t.amlResult.hardBlocked) return false;
      return ['pending', 'scheduled', 'failed'].includes(t.status);
    },

    rowClass(t) {
      return [
        'pq-row-' + (t.status || 'pending'),
        t.amlResult && t.amlResult.hardBlocked ? 'pq-hardblock' : '',
        t.amlResult && t.amlResult.softBlocked ? 'pq-softblock' : '',
      ];
    },

    amlColor(t) {
      if (!t.amlResult) return '#6c757d';
      return RISK_COLOR[t.amlResult.risk_level] || RISK_COLOR.unknown;
    },

    amlTitle(t) {
      if (!t.amlResult) return 'Unknown';
      return t.amlResult.risk_level.toUpperCase() +
        (t.amlResult.risk_score ? ` (${t.amlResult.risk_score}/100)` : '') +
        (t.amlResult.hardBlocked ? ' — HARD BLOCKED' : '') +
        (t.amlResult.softBlocked ? ' — SOFT BLOCKED' : '');
    },

    toDatetimeLocal(ts) {
      const d = new Date(ts);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    countdown(ts) {
      void this.tick;
      const diff = ts - Date.now();
      if (diff <= 0) return null;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    },

    fmtTime(ts) {
      return new Date(ts).toLocaleTimeString('en-US', { hour12: false });
    },

    explorerLink(entry) {
      const base = EXPLORER[entry.chain || this.chain] || '';
      return base + entry.txid;
    },

    exportCsv() {
      const rows = [['#', 'Address', 'Label', 'Amount USDT', 'Schedule', 'Status', 'TXID', 'AML Risk']];
      this.transfers.forEach((t, i) => {
        rows.push([
          i + 1,
          t.address,
          t.label || '',
          t.amount,
          t.scheduledAt ? new Date(t.scheduledAt).toISOString() : (t.delayMinutes ? `+${t.delayMinutes}min` : (t.rotationSlot || 'immediate')),
          t.status || 'pending',
          t.txid || '',
          t.amlResult ? t.amlResult.risk_level : 'unknown',
        ]);
      });
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const a = document.createElement('a');
      a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      a.download = `payout-${Date.now()}.csv`;
      a.click();
    },
  },
};
</script>

<style scoped>
.pq-root { display: flex; flex-direction: column; height: 100%; font-size: .82rem; }

.pq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid #2a2a2a;
  flex-wrap: wrap;
  gap: 6px;
}
.pq-mode-group { display: flex; align-items: center; gap: 8px; }
.pq-label { color: #888; font-size: .78rem; }
.pq-select {
  background: #1e1e1e; color: #ccc; border: 1px solid #444;
  border-radius: 4px; padding: 2px 6px; font-size: .78rem;
}
.pq-actions { display: flex; align-items: center; }

.pq-empty { color: #555; text-align: center; padding: 30px 20px; }

.pq-list { overflow-y: auto; flex: 1; padding: 4px 0; }

.pq-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid #1e1e1e;
  transition: background .15s;
  cursor: pointer;
  flex-wrap: nowrap;
  overflow: hidden;
}
.pq-row:hover { background: rgba(255,255,255,.03); }

.pq-row-confirmed { background: rgba(40,167,69,.06); }
.pq-row-failed    { background: rgba(220,53,69,.06); }
.pq-row-active    { background: rgba(240,185,11,.06); }
.pq-row-blocked,
.pq-hardblock     { background: rgba(220,53,69,.10) !important; }
.pq-softblock     { background: rgba(253,126,20,.07); }

.pq-index { color: #555; min-width: 22px; font-size: .75rem; }

.pq-aml-dot {
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
}
.pq-aml-unknown { background: #444; }

.pq-addr { color: #aaa; min-width: 90px; font-family: monospace; font-size: .78rem; }
.pq-label-tag {
  color: #666; font-size: .72rem; background: #222; border-radius: 3px;
  padding: 1px 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.pq-amount-input {
  width: 70px; background: #1a1a1a; border: 1px solid #333;
  border-radius: 3px; color: #fff; padding: 2px 4px; font-size: .8rem;
  text-align: right;
}
.pq-amount-text { width: 70px; text-align: right; color: #fff; }
.pq-usdt { color: #888; font-size: .72rem; }

.pq-schedule { display: flex; align-items: center; gap: 3px; flex-shrink: 0; }
.pq-datetime { background: #1a1a1a; border: 1px solid #333; border-radius: 3px; color: #ccc; font-size: .72rem; padding: 2px 4px; }
.pq-countdown { color: #f0b90b; font-size: .72rem; min-width: 40px; }
.pq-delay-label, .pq-delay-unit { color: #666; font-size: .72rem; }
.pq-delay-input { width: 44px; background: #1a1a1a; border: 1px solid #333; border-radius: 3px; color: #fff; font-size: .72rem; padding: 2px 4px; text-align: right; }
.pq-rotation-input { width: 90px; background: #1a1a1a; border: 1px solid #333; border-radius: 3px; color: #ccc; font-size: .72rem; padding: 2px 4px; }
.pq-manual-tag { color: #555; font-size: .72rem; }

.pq-status {
  padding: 2px 7px; border-radius: 20px; font-size: .68rem; font-weight: 600;
  letter-spacing: .04em; white-space: nowrap; flex-shrink: 0;
  background: rgba(255,255,255,.06); color: #888;
}
.pq-status-confirmed     { background: rgba(40,167,69,.15);  color: #28a745; }
.pq-status-failed        { background: rgba(220,53,69,.15);  color: #dc3545; }
.pq-status-active        { background: rgba(240,185,11,.15); color: #f0b90b; }
.pq-status-broadcasting  { background: rgba(23,162,184,.15); color: #17a2b8; }
.pq-status-waiting_ledger{ background: rgba(111,66,193,.15); color: #9d6de6; }
.pq-status-blocked       { background: rgba(220,53,69,.15);  color: #dc3545; }

.pq-btns { display: flex; gap: 4px; flex-shrink: 0; }
.btn-xs { padding: 1px 6px; font-size: .7rem; }

/* Log */
.pq-log { border-top: 1px solid #2a2a2a; max-height: 150px; overflow-y: auto; }
.pq-log-header { font-size: .72rem; color: #555; padding: 4px 10px; background: #111; }
.pq-log-entry {
  display: flex; align-items: center; gap: 8px;
  padding: 3px 10px; border-bottom: 1px solid #1a1a1a; font-size: .72rem;
}
.pq-log-time  { color: #555; flex-shrink: 0; font-family: monospace; }
.pq-log-info    { color: #aaa; }
.pq-log-success { color: #28a745; }
.pq-log-error   { color: #dc3545; }
.pq-log-link { color: #5c9de6; font-family: monospace; font-size: .68rem; }

/* Spinner */
.pq-spin {
  display: inline-block; width: 10px; height: 10px;
  border: 2px solid rgba(255,255,255,.2); border-top-color: #fff;
  border-radius: 50%; animation: pqSpin .7s linear infinite;
}
@keyframes pqSpin { to { transform: rotate(360deg); } }

/* List transitions */
.pq-fade-enter-active, .pq-fade-leave-active { transition: all .2s; }
.pq-fade-enter, .pq-fade-leave-to { opacity: 0; transform: translateX(-8px); }
</style>
